import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
};

export const FAQSection = () => {
  const faqs = [
    {
      question: "Puis-je projeter le plan de classe pendant le cours ?",
      answer: "Oui ! EduPlan permet de projeter le plan de classe via vidéoprojecteur. Les élèves voient immédiatement où s'asseoir dès leur entrée en classe. C'est cette fonctionnalité qui permet de gagner 7,3 minutes par cours."
    },
    {
      question: "Combien de temps pour déployer EduPlan dans mon établissement ?",
      answer: "Le déploiement complet prend entre 3 et 7 jours pour un collège de taille moyenne. Cela inclut la configuration des salles, l'import des élèves et la formation des équipes. Avec la formule Premium, nous nous occupons de tout."
    },
    {
      question: "Comment fonctionne le placement intelligent ?",
      answer: "Notre algorithme prend en compte les besoins spécifiques de chaque élève : les élèves avec des troubles visuels ou auditifs sont placés au premier rang, les élèves TSA en périphérie pour réduire les stimulations, et une mixité garçon/fille est respectée. Le tout en un clic."
    },
    {
      question: "Les élèves peuvent-ils proposer des modifications ?",
      answer: "Oui, c'est même un des points forts d'EduPlan ! Les délégués de classe peuvent proposer des ajustements via l'interface élève. Les professeurs reçoivent les propositions et peuvent les valider ou les refuser. Cette co-construction réduit drastiquement les conflits de placement."
    },
    {
      question: "Où sont hébergées mes données ?",
      answer: "Toutes les données sont hébergées en Europe, sur des serveurs sécurisés et conformes RGPD. Aucune donnée n'est transférée hors de l'Union Européenne. Consultez notre page dédiée : rgpd.eduplan-lnc.com"
    },
    {
      question: "Qu'est-ce que le 'scan d'établissement' de la formule Premium ?",
      answer: "Notre équipe se déplace dans votre établissement pour photographier et reproduire numériquement l'ensemble de vos salles de classe avec leurs configurations exactes (disposition des tables, fenêtres, tableau...). Cela représente un gain de 12 à 17 heures de travail pour vous."
    }
  ];

  return (
    <section id="faq" className="py-24" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-[#29282B] mb-4">
            Questions fréquentes
          </h2>
          <p className="text-[#29282B]/60">
            Tout ce que vous devez savoir sur EduPlan
          </p>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div key={index} variants={fadeUp}>
                <AccordionItem 
                  value={`item-${index}`} 
                  className="bg-white border border-[#D9DADC] rounded-lg overflow-hidden data-[state=open]:border-l-4 data-[state=open]:border-l-[#E7A541]" 
                  data-testid={`faq-item-${index}`}
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-[#F9F9FA] transition-colors">
                    <span className="font-display font-semibold text-[#29282B] text-left">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-[#29282B]/80">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="mt-12 text-center">
          <p className="text-[#29282B]/60 mb-4">Une autre question ?</p>
          <a 
            href="#contact" 
            className="inline-flex items-center text-[#E7A541] font-semibold hover:underline" 
            data-testid="faq-contact-link"
          >
            Contactez notre équipe
            <ChevronRight size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
